/**
 * PlaceTile applier.
 *
 * Adds the drafted tile to the active player's board at `action.coord`.
 * Clears the player's drafted tile from `draft.tileInstanceId`.
 * Records `turn.actionBudget.justPlacedTileCoord = action.coord`.
 * Advances phase: placeTile → placeWildlife.
 *
 * tileDefId resolution: initGame generates tile instances as `tile-inst-N`
 * where N cycles through TILE_DEFS indices. The def for instance N is
 * `TILE_DEFS[N % TILE_DEFS.length]`. If the instance ID doesn't follow that
 * pattern (e.g. fixture-injected IDs), we fall back to TILE_DEFS[0].
 *
 * Spec §6.4.
 */

import { resolveTileDefId } from "../data/instance-resolver";
import type { PlaceTileAction } from "../types/actions";
import { coordKey } from "../types/coords";
import type { GameState } from "../types/game-state";
import { bumpVersion } from "./common-applier";

export const applyPlaceTile = (
  state: GameState,
  action: PlaceTileAction,
): GameState => {
  const activeSeat = state.turn.activeSeat;
  const activePlayer = state.players[activeSeat]!;
  const tileInstanceId = activePlayer.draft.tileInstanceId!;
  const tileDefId = resolveTileDefId(tileInstanceId);
  const key = coordKey(action.coord);

  return {
    ...state,
    ...bumpVersion(state),
    turn: {
      ...state.turn,
      phase: "placeWildlife",
      actionBudget: {
        ...state.turn.actionBudget,
        justPlacedTileCoord: action.coord,
      },
    },
    players: state.players.map((p) =>
      p.seat === activeSeat
        ? {
            ...p,
            draft: {
              ...p.draft,
              tileInstanceId: null,
            },
            board: {
              cells: {
                ...p.board.cells,
                [key]: {
                  tileInstanceId,
                  tileDefId,
                  // Free rotation (2026-05-17). Absent ⇒ 0 (replay-safe,
                  // identity — pre-rotation games unaffected).
                  rotation: action.rotation ?? 0,
                  wildlife: null,
                  landmark: null,
                },
              },
            },
          }
        : p,
    ),
  };
};
